/**
 * Service: Evaluation lifecycle data access.
 * Handles creating/loading/updating evaluations in Supabase.
 * All functions throw on error so callers can handle failures.
 */

import { supabase } from "@/integrations/supabase/client";
import { parseCompetencyIds, parseSurveyAnswers } from "@/utils/evaluationParsing";
import { isUUID } from "@/domain/competency";
import { unwrapJoin } from "@/utils/unwrapJoin";

export interface EvaluationDraft {
  id: string;
  evaluation_period_id: string | null;
}

/** Fetch evaluation periods for dropdown (only active periods). */
export async function fetchEvaluationPeriodsForDropdown() {
  const { data, error } = await supabase
    .from("evaluation_periods")
    .select("id, name, start_date, end_date, status")
    .in("status", ["active"])
    .order("start_date", { ascending: false });
  if (error) throw new Error(`Impossible de charger les périodes d'évaluation : ${error.message}`);
  return (data ?? []).map((p) => ({
    value: p.id,
    label: p.name,
    from: p.start_date,
    to: p.end_date,
  }));
}

/**
 * Pick the best default period: the one containing today, or the most recent.
 * Periods are already sorted by start_date desc.
 */
export function pickDefaultPeriod(periods: { value: string; from: string; to: string }[]): string | null {
  if (periods.length === 0) return null;
  const today = new Date().toISOString().split("T")[0];
  const containing = periods.find((p) => p.from <= today && p.to >= today);
  return containing ? containing.value : periods[0].value;
}

/** Create a brand new evaluation draft. */
export async function createEvaluationDraft(params: {
  employeeId: string;
  competencyIds: string[];
  periodId: string | null;
  includeCompanyValues?: boolean;
}): Promise<EvaluationDraft> {
  const insertData: Record<string, unknown> = {
    employee_id: params.employeeId,
    status: "draft",
    survey_answers: {},
    competency_ids: params.competencyIds,
    current_step: 1,
    include_company_values: params.includeCompanyValues ?? false,
  };
  if (params.periodId) insertData.evaluation_period_id = params.periodId;

  const { data, error } = await supabase
    .from("evaluations")
    .insert(insertData)
    .select("id, evaluation_period_id")
    .single();

  if (error) throw new Error(`Impossible de créer le brouillon d'évaluation : ${error.message}`);
  return data as EvaluationDraft;
}

/** Fetch an existing evaluation by ID or find the latest draft for an employee. */
export async function fetchEvaluationDraft(
  evaluationId: string | null,
  employeeId: string,
): Promise<EvaluationDraft | null> {
  if (evaluationId) {
    const { data, error } = await supabase
      .from("evaluations")
      .select("id, evaluation_period_id")
      .eq("id", evaluationId)
      .eq("employee_id", employeeId)
      .maybeSingle();

    if (error) throw new Error(`Impossible de charger le brouillon d'évaluation : ${error.message}`);
    if (data) return data as EvaluationDraft;
  }

  const { data, error } = await supabase
    .from("evaluations")
    .select("id, evaluation_period_id, competency_ids")
    .eq("employee_id", employeeId)
    .eq("status", "draft")
    .order("created_at", { ascending: false })
    .limit(10);

  if (error) throw new Error(`Impossible de charger le brouillon d'évaluation : ${error.message}`);

  const drafts = data ?? [];
  const draftWithCompetencies = drafts.find(
    (d) => Array.isArray(d.competency_ids) && d.competency_ids.length > 0,
  );

  const selected = draftWithCompetencies ?? drafts[0];
  if (!selected) return null;

  return {
    id: selected.id,
    evaluation_period_id: selected.evaluation_period_id,
  };
}

/** Update competency_ids on an existing evaluation if they changed. */
export async function updateEvaluationCompetencyIds(
  evaluationId: string,
  newIds: string[],
): Promise<boolean> {
  const { data: existing, error: fetchError } = await supabase
    .from("evaluations")
    .select("competency_ids")
    .eq("id", evaluationId)
    .single();

  if (fetchError) throw new Error(`Impossible de lire les compétences de l'évaluation : ${fetchError.message}`);

  const storedIds = existing?.competency_ids;
  const changed =
    !storedIds ||
    !Array.isArray(storedIds) ||
    storedIds.length !== newIds.length ||
    !newIds.every((id: string) => storedIds.includes(id));

  if (changed) {
    const { error } = await supabase
      .from("evaluations")
      .update({ competency_ids: newIds })
      .eq("id", evaluationId);
    if (error) throw new Error(`Impossible de mettre à jour les compétences : ${error.message}`);
  }

  return changed;
}

/** Update the evaluation period on an existing evaluation. */
export async function updateEvaluationPeriod(
  evaluationId: string,
  periodId: string,
  competencyIds?: string[],
) {
  const updateData: { evaluation_period_id: string; competency_ids?: string[] } = { evaluation_period_id: periodId };
  if (competencyIds) updateData.competency_ids = competencyIds;
  const { error } = await supabase.from("evaluations").update(updateData).eq("id", evaluationId);
  if (error) throw new Error(`Impossible de mettre à jour la période : ${error.message}`);
}

/** Create a new evaluation with a specific period. */
export async function createEvaluationWithPeriod(
  employeeId: string,
  periodId: string,
  competencyIds?: string[],
): Promise<string> {
  const insertData: { employee_id: string; evaluation_period_id: string; status: string; competency_ids?: string[] } = {
    employee_id: employeeId,
    evaluation_period_id: periodId,
    status: "draft",
  };
  if (competencyIds) insertData.competency_ids = competencyIds;

  const { data, error } = await supabase.from("evaluations").insert(insertData).select("id").single();
  if (error) throw new Error(`Impossible de créer l'évaluation : ${error.message}`);
  return data.id;
}

/** Update evaluation step. */
export async function updateEvaluationStep(evaluationId: string, step: number) {
  const { error } = await supabase
    .from("evaluations")
    .update({ current_step: step })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de mettre à jour l'étape : ${error.message}`);
}

/** Mark an evaluation as completed. */
export async function completeEvaluation(evaluationId: string): Promise<void> {
  const { error } = await supabase
    .from("evaluations")
    .update({ status: "completed" as never, submitted_at: new Date().toISOString() })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de finaliser l'évaluation : ${error.message}`);
}

/** Delete an evaluation and its related topics. */
export async function deleteEvaluation(evaluationId: string): Promise<void> {
  const { error: topicsError } = await supabase
    .from("evaluation_topics")
    .delete()
    .eq("evaluation_id", evaluationId);
  if (topicsError) throw new Error(`Impossible de supprimer les sujets : ${topicsError.message}`);

  const { error } = await supabase
    .from("evaluations")
    .delete()
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de supprimer l'évaluation : ${error.message}`);
}

/** Revert a completed evaluation back to draft. */
export async function uncompleteEvaluation(evaluationId: string): Promise<void> {
  const { error } = await supabase
    .from("evaluations")
    .update({ status: "draft" as never, submitted_at: null })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de réouvrir l'évaluation : ${error.message}`);
}

/**
 * Update evaluation competency IDs and clean survey answers
 * by removing answers for competencies that were removed.
 * Business rule: static- prefixed keys are always preserved.
 */
export async function updateEvaluationCompetencies(
  evaluationId: string,
  newIds: string[],
): Promise<void> {
  const { data: evalData, error: fetchError } = await supabase
    .from("evaluations")
    .select("survey_answers")
    .eq("id", evaluationId)
    .single();

  if (fetchError) throw new Error(`Impossible de lire les réponses : ${fetchError.message}`);

  const existingAnswers = (evalData?.survey_answers as Record<string, number>) || {};
  const cleanedAnswers: Record<string, number> = {};
  const allowedSet = new Set(newIds);
  Object.entries(existingAnswers).forEach(([key, val]) => {
    if (allowedSet.has(key) || key.startsWith("static-") || key.startsWith("value-")) {
      cleanedAnswers[key] = val;
    }
  });

  const { error } = await supabase
    .from("evaluations")
    .update({
      competency_ids: newIds,
      survey_answers: cleanedAnswers,
    })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de mettre à jour les compétences : ${error.message}`);
}

// ─── Evaluation Recovery ─────────────────────────────────────────────

export interface RecoveredEvaluationData {
  employeeId: string;
  evaluationPeriodId: string | null;
  competencyIds: string[];
  /** Filtered survey_answers: only keys matching competencyIds or static-/value- prefix are kept. */
  filteredSurveyAnswers: Record<string, number>;
  includeCompanyValues: boolean;
  surveyNotes?: Record<string, string>;
  currentStep?: number;
}

/**
 * Fetch employee ID, competency IDs and filtered survey_answers from an evaluation record.
 * Business rule: competency_ids is the authoritative source;
 * falls back to survey_answers UUID keys if competency_ids is empty.
 * survey_answers are always filtered to only keep authoritative competency keys + static- keys.
 */
export async function fetchEvaluationContext(
  evaluationId: string,
): Promise<RecoveredEvaluationData | null> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("employee_id, evaluation_period_id, competency_ids, survey_answers, manual_overrides, include_company_values, survey_notes, current_step")
    .eq("id", evaluationId)
    .single();

  if (error) throw new Error(`Impossible de charger le contexte d'évaluation : ${error.message}`);
  if (!data) return null;

  const employeeId = data.employee_id;
  const evaluationPeriodId = data.evaluation_period_id ?? null;
  const includeCompanyValues = (data as Record<string, unknown>).include_company_values === true;
  const surveyNotes = (data as Record<string, unknown>).survey_notes as Record<string, string> | null;
  const currentStep = (data as Record<string, unknown>).current_step as number | null;

  let competencyIds = parseCompetencyIds(data.competency_ids);

  // Parse raw answers once and recover answered competency UUIDs.
  const rawAnswers = parseSurveyAnswers(data.survey_answers);
  const answeredCompetencyIds = Object.keys(rawAnswers).filter(isUUID);

  // If competency_ids is empty, recover from survey answers.
  if (competencyIds.length === 0) {
    competencyIds = answeredCompetencyIds;
  } else if (answeredCompetencyIds.length > 0) {
    competencyIds = Array.from(new Set([...competencyIds, ...answeredCompetencyIds]));
  }

  // Filter survey_answers to only keep allowed keys
  const allowedSet = new Set(competencyIds);
  const filteredSurveyAnswers: Record<string, number> = {};
  Object.entries(rawAnswers).forEach(([key, val]) => {
    if (allowedSet.has(key) || key.startsWith("static-") || key.startsWith("value-")) {
      filteredSurveyAnswers[key] = val;
    }
  });

  return { employeeId, evaluationPeriodId, competencyIds, filteredSurveyAnswers, includeCompanyValues, surveyNotes: surveyNotes ?? undefined, currentStep: currentStep ?? undefined };
}

// ─── Draft Evaluations ───────────────────────────────────────────────

export interface DraftEvaluationSummary {
  id: string;
  employee_id: string;
  evaluation_period_id: string;
  current_step: number;
  created_at: string;
  status?: string;
  submitted_at?: string | null;
  employee: {
    first_name: string; last_name: string; role_title: string | null;
    avatar_url: string | null; work_location: string | null; department: string | null;
    hire_date: string | null; birthday: string | null; manager_id: string | null;
  } | null;
  period: { name: string; start_date: string; end_date: string } | null;
}

/** Fetch all draft evaluations with employee and period info. */
export async function fetchDraftEvaluations(): Promise<DraftEvaluationSummary[]> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("id, employee_id, evaluation_period_id, current_step, created_at, status, submitted_at, employees!evaluations_employee_id_fkey(first_name, last_name, role_title, avatar_url, work_location, hire_date, birthday, manager_id, departments(name)), evaluation_periods!evaluations_evaluation_period_id_fkey(name, start_date, end_date)")
    .eq("status", "draft")
    .order("created_at", { ascending: false });
  if (error) throw new Error(`Impossible de charger les brouillons : ${error.message}`);
  return (data ?? []).map((d) => {
    const emp = unwrapJoin<{ first_name: string; last_name: string; role_title: string | null; avatar_url: string | null; work_location: string | null; hire_date: string | null; birthday: string | null; manager_id: string | null; departments: { name: string } | null }>(d.employees);
    const per = unwrapJoin<{ name: string; start_date: string; end_date: string }>(d.evaluation_periods);
    return {
      id: d.id,
      employee_id: d.employee_id,
      evaluation_period_id: d.evaluation_period_id,
      current_step: d.current_step || 1,
      created_at: d.created_at,
      status: d.status,
      submitted_at: d.submitted_at,
      employee: emp ? { first_name: emp.first_name, last_name: emp.last_name, role_title: emp.role_title, avatar_url: emp.avatar_url, work_location: emp.work_location, department: emp.departments?.name ?? null, hire_date: emp.hire_date, birthday: emp.birthday, manager_id: emp.manager_id } : null,
      period: per ?? null,
    };
  });
}

/** Fetch all completed evaluations with employee and period info. */
export async function fetchCompletedEvaluations(): Promise<DraftEvaluationSummary[]> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("id, employee_id, evaluation_period_id, current_step, created_at, status, submitted_at, employees!evaluations_employee_id_fkey(first_name, last_name, role_title, avatar_url, work_location, hire_date, birthday, manager_id, departments(name)), evaluation_periods!evaluations_evaluation_period_id_fkey(name, start_date, end_date)")
    .eq("status", "completed")
    .order("submitted_at", { ascending: false });
  if (error) throw new Error(`Impossible de charger les évaluations complétées : ${error.message}`);
  return (data ?? []).map((d) => {
    const emp = unwrapJoin<{ first_name: string; last_name: string; role_title: string | null; avatar_url: string | null; work_location: string | null; hire_date: string | null; birthday: string | null; manager_id: string | null; departments: { name: string } | null }>(d.employees);
    const per = unwrapJoin<{ name: string; start_date: string; end_date: string }>(d.evaluation_periods);
    return {
      id: d.id,
      employee_id: d.employee_id,
      evaluation_period_id: d.evaluation_period_id,
      current_step: d.current_step || 1,
      created_at: d.created_at,
      status: d.status,
      submitted_at: d.submitted_at,
      employee: emp ? { first_name: emp.first_name, last_name: emp.last_name, role_title: emp.role_title, avatar_url: emp.avatar_url, work_location: emp.work_location, department: emp.departments?.name ?? null, hire_date: emp.hire_date, birthday: emp.birthday, manager_id: emp.manager_id } : null,
      period: per ?? null,
    };
  });
}

/** Fetch survey answers for an evaluation (filtered by allowed competency IDs). */
export async function fetchFilteredSurveyAnswers(
  evaluationId: string,
  allowedCompetencyIds: string[],
): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("survey_answers")
    .eq("id", evaluationId)
    .single();
  if (error) throw new Error(`Impossible de charger les réponses : ${error.message}`);
  if (!data?.survey_answers || typeof data.survey_answers !== "object") return {};
  const raw = data.survey_answers as Record<string, number>;
  const allowedSet = new Set(allowedCompetencyIds);
  const filtered: Record<string, number> = {};
  Object.entries(raw).forEach(([key, val]) => {
    if (allowedSet.has(key) || key.startsWith("static-") || key.startsWith("value-")) {
      filtered[key] = val;
    }
  });
  return filtered;
}

// ─── Manual Overrides ──────────────────────────────────────────

export interface ManualOverride {
  status: string; // ui status: notMet | evolution | almostAchieved | achieved
  notes?: string;
}

/** Fetch manual overrides for an evaluation. */
export async function fetchManualOverrides(evaluationId: string): Promise<Record<string, ManualOverride>> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("manual_overrides")
    .eq("id", evaluationId)
    .single();
  if (error) throw new Error(`Impossible de charger les surcharges manuelles : ${error.message}`);
  return (data?.manual_overrides as Record<string, ManualOverride>) || {};
}

/** Save a manual override for a single competency on an evaluation. */
export async function saveManualOverride(
  evaluationId: string,
  competencyId: string,
  override: ManualOverride,
): Promise<void> {
  const existing = await fetchManualOverrides(evaluationId);
  const updated = { ...existing, [competencyId]: override };
  const { error } = await supabase
    .from("evaluations")
    .update({ manual_overrides: updated })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de sauvegarder la surcharge manuelle : ${error.message}`);
}

// ─── Evaluation Period CRUD ────────────────────────────────────

export interface EvaluationPeriodRow {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: "active" | "draft" | "inactive";
  organization_id: string;
}

/** Fetch all evaluation periods for the current org. */
export async function fetchEvaluationPeriods(): Promise<EvaluationPeriodRow[]> {
  const { data, error } = await supabase
    .from("evaluation_periods")
    .select("id, name, start_date, end_date, status, organization_id")
    .order("start_date", { ascending: false });
  if (error) throw new Error(`Impossible de charger les périodes : ${error.message}`);
  return (data ?? []) as EvaluationPeriodRow[];
}

/** Create a new evaluation period. */
export async function createEvaluationPeriod(params: {
  name: string;
  startDate: string;
  endDate: string;
  status: "active" | "draft" | "inactive";
  organizationId: string;
}): Promise<EvaluationPeriodRow> {
  const { data, error } = await supabase
    .from("evaluation_periods")
    .insert({
      name: params.name,
      start_date: params.startDate,
      end_date: params.endDate,
      status: params.status,
      organization_id: params.organizationId,
    })
    .select("id, name, start_date, end_date, status, organization_id")
    .single();
  if (error) throw new Error(`Impossible de créer la période : ${error.message}`);
  return data as EvaluationPeriodRow;
}

/** Update an existing evaluation period record. */
export async function updateEvaluationPeriodRecord(
  id: string,
  updates: Partial<{ name: string; start_date: string; end_date: string; status: string }>,
): Promise<void> {
  const { error } = await supabase
    .from("evaluation_periods")
    .update(updates)
    .eq("id", id);
  if (error) throw new Error(`Impossible de mettre à jour la période : ${error.message}`);
}

/** Delete an evaluation period. */
export async function deleteEvaluationPeriod(id: string): Promise<void> {
  const { error } = await supabase
    .from("evaluation_periods")
    .delete()
    .eq("id", id);
  if (error) throw new Error(`Impossible de supprimer la période : ${error.message}`);
}

// ─── Company Values Flag ─────────────────────────────────────────

/** Fetch include_company_values flag for an evaluation. */
export async function fetchEvaluationIncludeValues(evaluationId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("include_company_values")
    .eq("id", evaluationId)
    .single();
  if (error) throw new Error(`Impossible de charger le flag valeurs : ${error.message}`);
  return (data as Record<string, unknown>)?.include_company_values === true;
}

/** Update include_company_values flag on an evaluation. */
export async function updateEvaluationIncludeValues(evaluationId: string, include: boolean): Promise<void> {
  const { error } = await supabase
    .from("evaluations")
    .update({ include_company_values: include } as Record<string, unknown>)
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de mettre à jour le flag valeurs : ${error.message}`);
}

// ─── Survey Notes & Position Persistence ─────────────────────────────

/** Persist survey notes (Record<competencyId, noteText>) on the evaluation. */
export async function updateSurveyNotes(evaluationId: string, notes: Record<string, string>): Promise<void> {
  const { error } = await supabase
    .from("evaluations")
    .update({ survey_notes: notes } as Record<string, unknown>)
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de sauvegarder les notes du sondage : ${error.message}`);
}

/** Persist current question index using the existing current_step column. */
export async function updateCurrentQuestionIndex(evaluationId: string, index: number): Promise<void> {
  const { error } = await supabase
    .from("evaluations")
    .update({ current_step: index })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de sauvegarder la position : ${error.message}`);
}
